import * as React from 'react';

import type { ApolloQueryResult } from 'apollo-client';

import LearnerIncentiveNotification from 'bundles/learner-incentives/components/LearnerIncentiveNotification';
import DegreeApplicationDeadlineNotification from 'bundles/notification-center/components/DegreeApplicationDeadlineNotification';
import NotificationHeader from 'bundles/notification-center/components/NotificationHeader';
import NotificationListEmpty from 'bundles/notification-center/components/NotificationListEmpty';
import type {
  NotificationCenterMessagesQueryResponse,
  NotificationCenterMessagesQueryVariables,
} from 'bundles/notification-center/components/data/NotificationCenterMessagesQuery';
import AssignmentHasBeenPeerReviewedMessageNotification from 'bundles/notification-center/components/notifications/AssignmentHasBeenPeerReviewedMessageNotification';
import CalendarSyncNotification from 'bundles/notification-center/components/notifications/CalendarSyncNotification';
import CapstoneCompletionNotification from 'bundles/notification-center/components/notifications/CapstoneCompletionNotification';
import ContractRenewedNotification from 'bundles/notification-center/components/notifications/ContractRenewedNotification';
import DeadlineOverdueNotification from 'bundles/notification-center/components/notifications/DeadlineOverdueNotification';
import DeadlineReminderNotification from 'bundles/notification-center/components/notifications/DeadlineReminderNotification';
import DegreePathwaysCompletionNudgeNotification from 'bundles/notification-center/components/notifications/DegreePathwaysCompletionNudgeNotification';
import DiscussionForumNewAnswerForFollowerNotification from 'bundles/notification-center/components/notifications/DiscussionForumNewAnswerForFollowerNotification';
import DiscussionForumNewAnswerForQuestionCreatorNotification from 'bundles/notification-center/components/notifications/DiscussionForumNewAnswerForQuestionCreatorNotification';
import EnrolledProgramWithCourseNames from 'bundles/notification-center/components/notifications/EnrolledProgramWithCourseNames';
import EnterpriseContentAssignmentNotification from 'bundles/notification-center/components/notifications/EnterpriseContentAssignmentNotification';
import LearnerEnrollInProgramMessage from 'bundles/notification-center/components/notifications/LearnerEnrollInProgramMessage';
import LearnerEnrollInProgramWithCourseMessage from 'bundles/notification-center/components/notifications/LearnerEnrollInProgramWithCourseMessage';
import PeerReviewGradeReadyMessageNotification from 'bundles/notification-center/components/notifications/PeerReviewGradeReadyMessageNotification';
import ProductPromotionNotification from 'bundles/notification-center/components/notifications/ProductPromotionNotification';
import SetWeeklyGoalReminderNotification from 'bundles/notification-center/components/notifications/SetWeeklyGoalReminderNotification';
import UnverifiedEmailNotification from 'bundles/notification-center/components/notifications/UnverifiedEmailNotification';
import UnverifiedEmailReminderNotification from 'bundles/notification-center/components/notifications/UnverifiedEmailReminderNotification';
import UserVerificationNotification from 'bundles/notification-center/components/notifications/UserVerificationNotification';
import VerifiedCertificateCongratsNotification from 'bundles/notification-center/components/notifications/VerifiedCertificateCongratsNotification';
import type { Notification as NotificationType } from 'bundles/notification-center/types';

type Props = {
  unreadCount: number;
  notifications: Array<NotificationType>;
  refetchNotifications: (
    variables?: NotificationCenterMessagesQueryVariables
  ) => Promise<ApolloQueryResult<NotificationCenterMessagesQueryResponse>>;
  markAllNotificationsAsRead: () => void;
  markNotificationAsRead: (id: string) => void;
};

// TODO: Improve a11y. see https://medium.com/@im_rahul/focus-trapping-looping-b3ee658e5177
class NotificationListView extends React.Component<Props> {
  componentDidMount() {
    const { refetchNotifications } = this.props;
    // refetch notifications on mount to catch any updates in the current user session
    refetchNotifications();
  }

  handleMarkAllAsRead = () => {
    const { markAllNotificationsAsRead } = this.props;
    markAllNotificationsAsRead();
  };

  handleMarkAsRead = (notification: NotificationType) => {
    const { markNotificationAsRead } = this.props;
    markNotificationAsRead(notification.id);
  };

  render() {
    const { unreadCount, notifications } = this.props;

    const NotificationsComponents = notifications
      .map((notification, index) => {
        let component;
        /* notification is not included in these common props due to type error - Incompatible notification type - SetWeeklyGoalReminderNotificationType etc., */
        const notificationProps = { key: notification.id, rank: index, onClick: this.handleMarkAsRead };

        switch (notification.messageType) {
          case 'LearnerGoalSettingSetGoalMessage': // no epic check is needed since BE does the epic check
            component = <SetWeeklyGoalReminderNotification notification={notification} {...notificationProps} />;
            break;
          case 'DeadlineOverdueMessage':
            component = <DeadlineOverdueNotification notification={notification} {...notificationProps} />;
            break;
          case 'DeadlineReminderMessage':
            component = <DeadlineReminderNotification notification={notification} {...notificationProps} />;
            break;
          case 'UserVerificationMessage':
            component = <UserVerificationNotification notification={notification} {...notificationProps} />;
            break;
          case 'CalendarSyncMessage':
            component = <CalendarSyncNotification notification={notification} {...notificationProps} />;
            break;
          case 'PartnerIncentiveMessage':
            component = <LearnerIncentiveNotification notification={notification} {...notificationProps} />;
            break;
          case 'VerifiedCertificateCongratsMessage':
            component = <VerifiedCertificateCongratsNotification notification={notification} {...notificationProps} />;
            break;
          case 'AssignmentHasBeenPeerReviewedMessage':
            component = (
              <AssignmentHasBeenPeerReviewedMessageNotification notification={notification} {...notificationProps} />
            );
            break;
          case 'PeerReviewGradeReadyMessage':
            component = <PeerReviewGradeReadyMessageNotification notification={notification} {...notificationProps} />;
            break;
          case 'DiscussionForumNewAnswerForFollowerMessage':
            component = (
              <DiscussionForumNewAnswerForFollowerNotification notification={notification} {...notificationProps} />
            );
            break;
          case 'DiscussionForumNewAnswerForQuestionCreatorMessage':
            component = (
              <DiscussionForumNewAnswerForQuestionCreatorNotification
                notification={notification}
                {...notificationProps}
              />
            );
            break;
          case 'CapstoneCompletionNotificationMessage':
            component = <CapstoneCompletionNotification notification={notification} {...notificationProps} />;
            break;
          case 'EnterpriseContentAssignmentMessage':
            component = <EnterpriseContentAssignmentNotification notification={notification} {...notificationProps} />;
            break;
          case 'ProductPromotionMessage':
            component = <ProductPromotionNotification notification={notification} {...notificationProps} />;
            break;
          case 'DegreeDeadlineOneWeekAheadMessage':
          case 'DegreeDeadlineFourWeeksAheadMessage':
            component = <DegreeApplicationDeadlineNotification notification={notification} {...notificationProps} />;
            break;
          case 'DegreePathwaysCompletionNudgeMessage':
            component = (
              <DegreePathwaysCompletionNudgeNotification notification={notification} {...notificationProps} />
            );
            break;
          case 'LearnerEnrollInProgramMessage':
            component = <LearnerEnrollInProgramMessage notification={notification} {...notificationProps} />;
            break;
          case 'LearnerEnrollInProgramWithCourseMessage':
            component = <LearnerEnrollInProgramWithCourseMessage notification={notification} {...notificationProps} />;
            break;
          case 'EnrolledProgramWithCourseNames':
            component = <EnrolledProgramWithCourseNames notification={notification} {...notificationProps} />;
            break;
          case 'UnverifiedEmailMessage':
            component = <UnverifiedEmailNotification notification={notification} {...notificationProps} />;
            break;
          case 'UnverifiedEmailReminderMessage':
            component = <UnverifiedEmailReminderNotification notification={notification} {...notificationProps} />;
            break;
          case 'ContractRenewedMessage':
            component = <ContractRenewedNotification notification={notification} {...notificationProps} />;
            break;
          default:
            component = null;
            break;
        }

        return component;
      })
      .filter((v) => !!v);

    return (
      <div>
        <NotificationHeader unreadCount={unreadCount} onMarkAllAsRead={this.handleMarkAllAsRead} />
        {NotificationsComponents.length === 0 && <NotificationListEmpty />}
        <div>{NotificationsComponents}</div>
      </div>
    );
  }
}

export default NotificationListView;
